import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/ngrx/app-state/app-state';
import { GetSessionAttendanceAction, UpdateAttendanceRecordAction } from 'app/ngrx/attendance/attendance.reducers';
import { getAttendanceForInstance, getAttendanceForInstanceAndUser } from 'app/ngrx/attendance/attendance.state';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AttendanceRecord, AttendanceRecordState } from '../models/AttendanceRecord';
import { HttpService } from './http-service';

// AttendanceAddRecordsReq
export type AttendanceAddRecordsReq = {
	activity_instance_id: number;
	attendance_user_ids: number[];
	state: AttendanceRecordState | string; // TODO: remove `string`
};

// AttendanceRecordUpdateReq
export type AttendanceRecordUpdateReq = {
	id: number;
	state: AttendanceRecordState | string; // TODO: remove `string`
};

export type AttendanceRecordExportReq = {
	started_at: string;
	stopped_at: string;
};

@Injectable({
	providedIn: 'root',
})
export class AttendanceRecordService {
	constructor(private http: HttpService, private store: Store<AppState>) {}

	activityAttendance$ = (activity_instance_id: number) => {
		return this.store.select((state) => getAttendanceForInstance(activity_instance_id)(state.attendance));
	};
	activityAttendanceForUser$ = (activity_instance_id: number, user_id: number) => {
		return this.store.select((state) => getAttendanceForInstanceAndUser(activity_instance_id, user_id)(state.attendance));
	};

	insertRecords(records: AttendanceRecord[]): void {
		this.store.dispatch(GetSessionAttendanceAction({ records }));
	}

	// Since other components depend on the HTTP observable, we don't use effects
	// to dispatch our HTTP calls here. If we like this pattern going forward,
	// let's expand on it - effects can be a confusing abstraction that is often
	// not needed.

	getRecordsForInstanceId(activityInstanceId: number): Observable<AttendanceRecord[]> {
		return this.http
			.post<{ attendance_records: any[] }>('v2/attendance/activity/list', { activity_instance_id: activityInstanceId }, undefined, false)
			.pipe(
				map((resp) => {
					const records = resp.attendance_records.map((a) => AttendanceRecord.fromJSON(a)).filter((a) => a !== null) as AttendanceRecord[];
					this.store.dispatch(GetSessionAttendanceAction({ records }));
					return records;
				})
			);
	}

	addRecords(req: AttendanceAddRecordsReq): Observable<AttendanceRecord[]> {
		return this.http.post<{ attendance_records: any[] }>('v2/attendance/activity/add', req, undefined, false).pipe(
			map((resp) => {
				const records = resp.attendance_records.map((a) => AttendanceRecord.fromJSON(a)).filter((a) => a !== null) as AttendanceRecord[];
				this.store.dispatch(GetSessionAttendanceAction({ records }));
				return records;
			})
		);
	}

	updateRecordState(req: AttendanceRecordUpdateReq): Observable<AttendanceRecord | null> {
		return this.http.post<{ attendance_records: any[] }>('v2/attendance/activity/update', req, undefined, false).pipe(
			map((resp) => {
				const record = resp.attendance_records.map((a) => AttendanceRecord.fromJSON(a))[0];
				if (record) {
					this.store.dispatch(UpdateAttendanceRecordAction({ record }));
				}
				return record;
			})
		);
	}

	downloadRecords(req: AttendanceRecordExportReq): Observable<boolean> {
		return this.http.post<{ job_id: string }>('v2/attendance/activity/export', req, undefined, false).pipe(map(() => true));
	}
}
